import { useState, useEffect, useRef } from "react";
import { Phone, Car, Heart, Clock, Shield, Star, MapPin, Mail, Menu, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { format } from "date-fns";
import { fr } from "date-fns/locale";
import { toast } from "sonner";
import axios from "axios";
import { Link } from "react-router-dom";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Assets
const LOGO_WHITE = "https://customer-assets.emergentagent.com/job_gosto-marseille-1/artifacts/5h91p5vw_ChatGPT%20Image%2013%20f%C3%A9vr.%202026%2C%2021_11_06%20-%20Copie.png";
const LOGO_DARK = "https://customer-assets.emergentagent.com/job_gosto-marseille-1/artifacts/qo3t0ggz_ChatGPT%20Image%2013%20f%C3%A9vr.%202026%2C%2021_11_06.png";
const HERO_BG = "https://images.unsplash.com/photo-1692118450510-3c1d8e7d1ae8?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1ODF8MHwxfHNlYXJjaHwzfHxtYXJzZWlsbGUlMjBmcmFuY2UlMjBjaXR5JTIwbGFuZG1hcmt8ZW58MHx8fHwxNzcxMDEzNjk0fDA&ixlib=rb-4.1.0&q=85";

const PHONE_NUMBER = "07 72 06 72 23";
const PHONE_LINK = "tel:+33772067223";

export default function HomePage() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [reservationDate, setReservationDate] = useState(null);
  const [reservationLoading, setReservationLoading] = useState(false);
  const [contactLoading, setContactLoading] = useState(false);

  // Animation on scroll
  useEffect(() => {
    const observerOptions = {
      threshold: 0.1,
      rootMargin: "0px 0px -50px 0px"
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add("animate-visible");
        }
      });
    }, observerOptions);

    document.querySelectorAll(".animate-on-scroll").forEach(el => {
      observer.observe(el);
    });

    return () => observer.disconnect();
  }, []);

  // Reservation form state
  const [reservationForm, setReservationForm] = useState({
    nom: "",
    telephone: "",
    email: "",
    type_course: "",
    heure_souhaitee: "",
    adresse_depart: "",
    adresse_arrivee: "",
    message: ""
  });

  // Contact form state
  const [contactForm, setContactForm] = useState({
    nom: "",
    email: "",
    telephone: "",
    raison: "",
    message: ""
  });

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
    setMobileMenuOpen(false);
  };

  const handleReservationSubmit = async (e) => {
    e.preventDefault();
    
    if (!reservationForm.nom || !reservationForm.telephone || !reservationForm.email || !reservationForm.type_course || !reservationDate) {
      toast.error("Veuillez remplir tous les champs obligatoires");
      return;
    }

    setReservationLoading(true);
    try {
      const response = await axios.post(`${API}/reservation`, {
        ...reservationForm,
        date_souhaitee: format(reservationDate, "dd/MM/yyyy", { locale: fr })
      });
      
      toast.success(response.data.message);
      setReservationForm({
        nom: "",
        telephone: "",
        email: "",
        type_course: "",
        heure_souhaitee: "",
        adresse_depart: "",
        adresse_arrivee: "",
        message: ""
      });
      setReservationDate(null);
    } catch (error) {
      toast.error("Une erreur est survenue. Veuillez réessayer.");
      console.error(error);
    } finally {
      setReservationLoading(false);
    }
  };

  const handleContactSubmit = async (e) => {
    e.preventDefault();
    
    if (!contactForm.nom || !contactForm.email || !contactForm.message || !contactForm.raison) {
      toast.error("Veuillez remplir tous les champs obligatoires");
      return;
    }

    setContactLoading(true);
    try {
      const response = await axios.post(`${API}/contact`, contactForm);
      
      toast.success(response.data.message);
      setContactForm({
        nom: "",
        email: "",
        telephone: "",
        raison: "",
        message: ""
      });
    } catch (error) {
      toast.error("Une erreur est survenue. Veuillez réessayer.");
      console.error(error);
    } finally {
      setContactLoading(false);
    }
  };

  return (
    <div className="min-h-screen">
      {/* Header */}
      <header className="sticky-header" data-testid="header">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-20">
            {/* Logo */}
            <a href="/" className="flex-shrink-0" data-testid="logo-link">
              <img src={LOGO_WHITE} alt="GOSTO TAXI Marseille" className="h-14 w-auto" />
            </a>

            {/* Desktop Navigation */}
            <nav className="hidden md:flex items-center gap-8" data-testid="desktop-nav">
              <button onClick={() => scrollToSection("services")} className="text-[#1a365d] font-medium hover:text-[#f7c52d] transition-colors">
                Services
              </button>
              <button onClick={() => scrollToSection("avantages")} className="text-[#1a365d] font-medium hover:text-[#f7c52d] transition-colors">
                Pourquoi nous
              </button>
              <button onClick={() => scrollToSection("reservation")} className="text-[#1a365d] font-medium hover:text-[#f7c52d] transition-colors">
                Réservation
              </button>
              <button onClick={() => scrollToSection("contact")} className="text-[#1a365d] font-medium hover:text-[#f7c52d] transition-colors">
                Contact
              </button>
            </nav>

            {/* Phone + CTA */}
            <div className="hidden md:flex items-center gap-4">
              <a href={PHONE_LINK} className="phone-link text-[#1a365d]" data-testid="phone-desktop">
                <Phone className="w-5 h-5" />
                {PHONE_NUMBER}
              </a>
              <Button 
                onClick={() => scrollToSection("reservation")}
                className="bg-[#f7c52d] text-[#1a365d] hover:bg-[#eab308] font-bold px-6"
                data-testid="cta-header"
              >
                RÉSERVER
              </Button>
            </div>

            {/* Mobile Menu Button */}
            <button 
              className="md:hidden p-2"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              data-testid="mobile-menu-btn"
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden bg-white border-t" data-testid="mobile-menu">
            <div className="px-4 py-4 space-y-4">
              <button onClick={() => scrollToSection("services")} className="block w-full text-left py-2 text-[#1a365d] font-medium">
                Services
              </button>
              <button onClick={() => scrollToSection("avantages")} className="block w-full text-left py-2 text-[#1a365d] font-medium">
                Pourquoi nous
              </button>
              <button onClick={() => scrollToSection("reservation")} className="block w-full text-left py-2 text-[#1a365d] font-medium">
                Réservation
              </button>
              <button onClick={() => scrollToSection("contact")} className="block w-full text-left py-2 text-[#1a365d] font-medium">
                Contact
              </button>
              <a href={PHONE_LINK} className="flex items-center gap-2 py-2 text-[#1a365d] font-bold">
                <Phone className="w-5 h-5" />
                {PHONE_NUMBER}
              </a>
            </div>
          </div>
        )}
      </header>

      {/* Hero Section */}
      <section className="hero-section" data-testid="hero-section">
        <div className="hero-bg" style={{ backgroundImage: `url(${HERO_BG})` }}></div>
        <div className="hero-overlay"></div>
        
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 md:py-32">
          <div className="max-w-3xl">
            {/* Badges */}
            <div className="flex flex-wrap gap-3 mb-8">
              <span className="badge-approved inline-flex items-center gap-2" data-testid="badge-approved">
                <Shield className="w-4 h-4" />
                Taxi rattaché à la ville de Marseille
              </span>
              <span className="badge-cpam inline-flex items-center gap-2" data-testid="badge-cpam">
                <Heart className="w-4 h-4" />
                Conventionné CPAM
              </span>
            </div>

            <h1 className="text-4xl md:text-5xl lg:text-6xl font-black text-white tracking-tight leading-tight mb-6" data-testid="hero-title">
              GOSTO TAXI
              <span className="block text-[#f7c52d]">Votre taxi de confiance à Marseille</span>
            </h1>
            
            <p className="text-lg md:text-xl text-slate-300 mb-8 leading-relaxed" data-testid="hero-subtitle">
              Transport privé & médical conventionné CPAM<br />
              Disponible 7j/7 sur Marseille et les Bouches-du-Rhône
            </p>

            <div className="flex flex-col sm:flex-row gap-4 mb-8">
              <Button 
                onClick={() => scrollToSection("reservation")}
                className="bg-[#f7c52d] text-[#1a365d] hover:bg-[#eab308] font-bold text-lg px-8 h-14 uppercase tracking-wide"
                data-testid="cta-hero"
              >
                Réserver une course
              </Button>
              <a 
                href={PHONE_LINK}
                className="inline-flex items-center justify-center gap-3 bg-white/10 backdrop-blur text-white font-bold text-lg px-8 h-14 border border-white/20 hover:bg-white/20 transition-colors"
                data-testid="phone-hero"
              >
                <Phone className="w-5 h-5" />
                {PHONE_NUMBER}
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="services" className="py-16 md:py-24 lg:py-32 bg-white" data-testid="services-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16 animate-on-scroll">
            <p className="text-sm uppercase tracking-widest font-bold text-[#f7c52d] mb-4">Nos services</p>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-[#1a365d] tracking-tight">
              Transport privé & médical
            </h2>
          </div>

          <div className="grid md:grid-cols-2 gap-8 lg:gap-12">
            {/* Transport Privé */}
            <div className="service-card group animate-on-scroll" data-testid="service-private">
              <div className="w-16 h-16 bg-[#1a365d] flex items-center justify-center mb-6 group-hover:bg-[#f7c52d] transition-colors">
                <Car className="w-8 h-8 text-white group-hover:text-[#1a365d] transition-colors" />
              </div>
              <h3 className="text-2xl font-bold text-[#1a365d] mb-4">Courses privées</h3>
              <ul className="space-y-3 text-slate-600">
                <li className="flex items-start gap-3">
                  <span className="w-2 h-2 bg-[#f7c52d] mt-2 flex-shrink-0"></span>
                  Trajets quotidiens et ponctuels
                </li>
                <li className="flex items-start gap-3">
                  <span className="w-2 h-2 bg-[#f7c52d] mt-2 flex-shrink-0"></span>
                  Transferts aéroport Marseille-Provence
                </li>
                <li className="flex items-start gap-3">
                  <span className="w-2 h-2 bg-[#f7c52d] mt-2 flex-shrink-0"></span>
                  Gare Saint-Charles
                </li>
                <li className="flex items-start gap-3">
                  <span className="w-2 h-2 bg-[#f7c52d] mt-2 flex-shrink-0"></span>
                  Sorties, événements, rendez-vous
                </li>
                <li className="flex items-start gap-3">
                  <span className="w-2 h-2 bg-[#f7c52d] mt-2 flex-shrink-0"></span>
                  Disponible 24h/24, 7j/7
                </li>
                <li className="flex items-start gap-3">
                  <span className="w-2 h-2 bg-[#f7c52d] mt-2 flex-shrink-0"></span>
                  Tarifs réglementés et transparents
                </li>
              </ul>
            </div>

            {/* Transport Médical */}
            <div className="service-card group border-l-4 border-l-[#1a365d] animate-on-scroll" data-testid="service-medical">
              <div className="w-16 h-16 bg-[#1a365d] flex items-center justify-center mb-6 group-hover:bg-[#f7c52d] transition-colors">
                <Heart className="w-8 h-8 text-white group-hover:text-[#1a365d] transition-colors" />
              </div>
              <h3 className="text-2xl font-bold text-[#1a365d] mb-4">Transport médical conventionné</h3>
              <p className="text-slate-600 mb-4">
                Taxi agréé CPAM pour vos déplacements médicaux. Prise en charge Sécurité sociale de 65% à 100% selon votre situation.
              </p>
              <ul className="space-y-3 text-slate-600 mb-6">
                <li className="flex items-start gap-3">
                  <span className="w-2 h-2 bg-[#f7c52d] mt-2 flex-shrink-0"></span>
                  Consultations médicales
                </li>
                <li className="flex items-start gap-3">
                  <span className="w-2 h-2 bg-[#f7c52d] mt-2 flex-shrink-0"></span>
                  Hospitalisations (entrées/sorties)
                </li>
                <li className="flex items-start gap-3">
                  <span className="w-2 h-2 bg-[#f7c52d] mt-2 flex-shrink-0"></span>
                  Séances de rééducation, dialyse, chimiothérapie
                </li>
                <li className="flex items-start gap-3">
                  <span className="w-2 h-2 bg-[#f7c52d] mt-2 flex-shrink-0"></span>
                  Examens (scanner, IRM, radiologie)
                </li>
                <li className="flex items-start gap-3">
                  <span className="w-2 h-2 bg-[#f7c52d] mt-2 flex-shrink-0"></span>
                  Sur prescription médicale de transport (PMT)
                </li>
              </ul>
              <div className="badge-cpam inline-block" data-testid="badge-cpam-service">
                Taxi Conventionné - Organismes d'assurance maladie
              </div>
            </div>
          </div>

          {/* Info CPAM */}
          <div className="mt-12 p-6 md:p-8 bg-slate-50 border-l-4 border-[#1a365d]" data-testid="cpam-info">
            <p className="text-slate-700 leading-relaxed">
              <strong className="text-[#1a365d]">Vous bénéficiez d'une prescription médicale de transport (PMT) ?</strong> GOSTO TAXI est conventionné avec la CPAM pour le transport assis professionnalisé. Vos frais sont pris en charge à 65% minimum, jusqu'à 100% selon votre situation (ALD, accident du travail, invalidité). Présentez simplement votre prescription, carte vitale et attestation de droits.
            </p>
          </div>
        </div>
      </section>

      {/* Avantages Section */}
      <section id="avantages" className="py-16 md:py-24 lg:py-32 bg-slate-50" data-testid="avantages-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16 animate-on-scroll">
            <p className="text-sm uppercase tracking-widest font-bold text-[#f7c52d] mb-4">Vos avantages</p>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-[#1a365d] tracking-tight">
              Pourquoi choisir GOSTO TAXI
            </h2>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="feature-card animate-on-scroll" data-testid="feature-expertise">
              <div className="w-12 h-12 bg-[#1a365d] flex items-center justify-center mb-4">
                <Heart className="w-6 h-6 text-[#f7c52d]" />
              </div>
              <h3 className="text-lg font-bold text-[#1a365d] mb-2">Expertise médicale</h3>
              <p className="text-slate-600 text-sm">
                Ancien ambulancier diplômé d'État, formé aux premiers secours et au transport de patients
              </p>
            </div>

            <div className="feature-card animate-on-scroll" data-testid="feature-approved">
              <div className="w-12 h-12 bg-[#1a365d] flex items-center justify-center mb-4">
                <Shield className="w-6 h-6 text-[#f7c52d]" />
              </div>
              <h3 className="text-lg font-bold text-[#1a365d] mb-2">Rattaché à Marseille</h3>
              <p className="text-slate-600 text-sm">
                Taxi rattaché à la ville de Marseille, conventionné CPAM
              </p>
            </div>

            <div className="feature-card animate-on-scroll" data-testid="feature-ponctuality">
              <div className="w-12 h-12 bg-[#1a365d] flex items-center justify-center mb-4">
                <Clock className="w-6 h-6 text-[#f7c52d]" />
              </div>
              <h3 className="text-lg font-bold text-[#1a365d] mb-2">Ponctualité</h3>
              <p className="text-slate-600 text-sm">
                Respect de vos horaires de rendez-vous médicaux et déplacements
              </p>
            </div>

            <div className="feature-card animate-on-scroll" data-testid="feature-comfort">
              <div className="w-12 h-12 bg-[#1a365d] flex items-center justify-center mb-4">
                <Star className="w-6 h-6 text-[#f7c52d]" />
              </div>
              <h3 className="text-lg font-bold text-[#1a365d] mb-2">Confort</h3>
              <p className="text-slate-600 text-sm">
                Véhicule récent, propre et adapté au transport de personnes
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Reservation Section */}
      <section id="reservation" className="py-16 md:py-24 lg:py-32 bg-white" data-testid="reservation-section">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12 animate-on-scroll">
            <p className="text-sm uppercase tracking-widest font-bold text-[#f7c52d] mb-4">Réservation</p>
            <h2 className="text-3xl md:text-4xl font-bold text-[#1a365d] tracking-tight">
              Réserver une course
            </h2>
          </div>

          <form onSubmit={handleReservationSubmit} className="space-y-6 animate-on-scroll" data-testid="reservation-form">
            <div className="grid sm:grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label htmlFor="res-nom" className="text-[#1a365d] font-medium">Nom complet *</Label>
                <Input
                  id="res-nom"
                  data-testid="res-nom"
                  value={reservationForm.nom}
                  onChange={(e) => setReservationForm({...reservationForm, nom: e.target.value})}
                  className="h-12 border-slate-300 focus:border-[#1a365d]"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="res-telephone" className="text-[#1a365d] font-medium">Téléphone *</Label>
                <Input
                  id="res-telephone"
                  data-testid="res-telephone"
                  type="tel"
                  value={reservationForm.telephone}
                  onChange={(e) => setReservationForm({...reservationForm, telephone: e.target.value})}
                  className="h-12 border-slate-300 focus:border-[#1a365d]"
                  required
                />
              </div>
            </div>

            <div className="grid sm:grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label htmlFor="res-email" className="text-[#1a365d] font-medium">Email *</Label>
                <Input
                  id="res-email"
                  data-testid="res-email"
                  type="email"
                  value={reservationForm.email}
                  onChange={(e) => setReservationForm({...reservationForm, email: e.target.value})}
                  className="h-12 border-slate-300 focus:border-[#1a365d]"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label className="text-[#1a365d] font-medium">Type de course *</Label>
                <Select
                  value={reservationForm.type_course}
                  onValueChange={(value) => setReservationForm({...reservationForm, type_course: value})}
                >
                  <SelectTrigger className="h-12 border-slate-300" data-testid="res-type">
                    <SelectValue placeholder="Sélectionner" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="privee">Course privée</SelectItem>
                    <SelectItem value="medical">Transport médical CPAM</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid sm:grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label className="text-[#1a365d] font-medium">Date souhaitée *</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className="w-full h-12 justify-start text-left font-normal border-slate-300"
                      data-testid="res-date"
                    >
                      {reservationDate ? format(reservationDate, "PPP", { locale: fr }) : "Choisir une date"}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={reservationDate}
                      onSelect={setReservationDate}
                      locale={fr}
                      disabled={(date) => date < new Date()}
                    />
                  </PopoverContent>
                </Popover>
              </div>
              <div className="space-y-2">
                <Label htmlFor="res-heure" className="text-[#1a365d] font-medium">Heure souhaitée</Label>
                <Input
                  id="res-heure"
                  data-testid="res-heure"
                  type="time"
                  value={reservationForm.heure_souhaitee}
                  onChange={(e) => setReservationForm({...reservationForm, heure_souhaitee: e.target.value})}
                  className="h-12 border-slate-300 focus:border-[#1a365d]"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="res-depart" className="text-[#1a365d] font-medium">Adresse de départ</Label>
              <Input
                id="res-depart"
                data-testid="res-depart"
                value={reservationForm.adresse_depart}
                onChange={(e) => setReservationForm({...reservationForm, adresse_depart: e.target.value})}
                className="h-12 border-slate-300 focus:border-[#1a365d]"
                placeholder="Ex: 15 rue de la République, 13001 Marseille"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="res-arrivee" className="text-[#1a365d] font-medium">Adresse d'arrivée</Label>
              <Input
                id="res-arrivee"
                data-testid="res-arrivee"
                value={reservationForm.adresse_arrivee}
                onChange={(e) => setReservationForm({...reservationForm, adresse_arrivee: e.target.value})}
                className="h-12 border-slate-300 focus:border-[#1a365d]"
                placeholder="Ex: Aéroport Marseille-Provence"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="res-message" className="text-[#1a365d] font-medium">Informations complémentaires</Label>
              <Textarea
                id="res-message"
                data-testid="res-message"
                value={reservationForm.message}
                onChange={(e) => setReservationForm({...reservationForm, message: e.target.value})}
                className="min-h-[120px] border-slate-300 focus:border-[#1a365d]"
                placeholder="Précisez vos besoins spécifiques (mobilité réduite, bagages, etc.)"
              />
            </div>

            <Button
              type="submit"
              disabled={reservationLoading}
              className="w-full bg-[#f7c52d] text-[#1a365d] hover:bg-[#eab308] font-bold text-lg py-6 uppercase tracking-wide"
              data-testid="res-submit"
            >
              {reservationLoading ? "Envoi en cours..." : "Envoyer ma demande"}
            </Button>
          </form>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-16 md:py-24 lg:py-32 bg-[#0f172a]" data-testid="contact-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12 animate-on-scroll">
            <p className="text-sm uppercase tracking-widest font-bold text-[#f7c52d] mb-4">Contact</p>
            <h2 className="text-3xl md:text-4xl font-bold text-white tracking-tight">
              Nous contacter
            </h2>
          </div>

          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16">
            {/* Contact Form */}
            <form onSubmit={handleContactSubmit} className="space-y-6" data-testid="contact-form">
              <div className="grid sm:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label htmlFor="contact-nom" className="text-white font-medium">Nom *</Label>
                  <Input
                    id="contact-nom"
                    data-testid="contact-nom"
                    value={contactForm.nom}
                    onChange={(e) => setContactForm({...contactForm, nom: e.target.value})}
                    className="h-12 bg-white/10 border-white/20 text-white placeholder:text-white/50 focus:border-[#f7c52d]"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="contact-email" className="text-white font-medium">Email *</Label>
                  <Input
                    id="contact-email"
                    data-testid="contact-email"
                    type="email"
                    value={contactForm.email}
                    onChange={(e) => setContactForm({...contactForm, email: e.target.value})}
                    className="h-12 bg-white/10 border-white/20 text-white placeholder:text-white/50 focus:border-[#f7c52d]"
                    required
                  />
                </div>
              </div>

              <div className="grid sm:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label htmlFor="contact-telephone" className="text-white font-medium">Téléphone</Label>
                  <Input
                    id="contact-telephone"
                    data-testid="contact-telephone"
                    type="tel"
                    value={contactForm.telephone}
                    onChange={(e) => setContactForm({...contactForm, telephone: e.target.value})}
                    className="h-12 bg-white/10 border-white/20 text-white placeholder:text-white/50 focus:border-[#f7c52d]"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-white font-medium">Raison du contact *</Label>
                  <Select
                    value={contactForm.raison}
                    onValueChange={(value) => setContactForm({...contactForm, raison: value})}
                  >
                    <SelectTrigger className="h-12 bg-white/10 border-white/20 text-white" data-testid="contact-raison">
                      <SelectValue placeholder="Sélectionner" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="info">Demande d'information</SelectItem>
                      <SelectItem value="partenariat">Demande de partenariat</SelectItem>
                      <SelectItem value="presse">Presse / Média</SelectItem>
                      <SelectItem value="autre">Autre</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="contact-message" className="text-white font-medium">Message *</Label>
                <Textarea
                  id="contact-message"
                  data-testid="contact-message"
                  value={contactForm.message}
                  onChange={(e) => setContactForm({...contactForm, message: e.target.value})}
                  className="min-h-[150px] bg-white/10 border-white/20 text-white placeholder:text-white/50 focus:border-[#f7c52d]"
                  required
                />
              </div>

              <Button
                type="submit"
                disabled={contactLoading}
                className="w-full bg-[#f7c52d] text-[#1a365d] hover:bg-[#eab308] font-bold text-lg py-6 uppercase tracking-wide"
                data-testid="contact-submit"
              >
                {contactLoading ? "Envoi en cours..." : "Envoyer"}
              </Button>
            </form>

            {/* Contact Info */}
            <div className="lg:pl-8" data-testid="contact-info">
              <div className="space-y-8">
                <div>
                  <h3 className="text-xl font-bold text-white mb-4">Coordonnées</h3>
                  <div className="space-y-4">
                    <a href={PHONE_LINK} className="flex items-center gap-4 text-white hover:text-[#f7c52d] transition-colors">
                      <div className="w-12 h-12 bg-[#f7c52d] flex items-center justify-center">
                        <Phone className="w-6 h-6 text-[#1a365d]" />
                      </div>
                      <span className="text-lg font-bold">{PHONE_NUMBER}</span>
                    </a>
                    <a href="mailto:contact@gostotaxi.com" className="flex items-center gap-4 text-white hover:text-[#f7c52d] transition-colors">
                      <div className="w-12 h-12 bg-white/10 flex items-center justify-center">
                        <Mail className="w-6 h-6 text-[#f7c52d]" />
                      </div>
                      <span>contact@gostotaxi.com</span>
                    </a>
                    <div className="flex items-center gap-4 text-white">
                      <div className="w-12 h-12 bg-white/10 flex items-center justify-center">
                        <MapPin className="w-6 h-6 text-[#f7c52d]" />
                      </div>
                      <span>Marseille et Bouches-du-Rhône</span>
                    </div>
                  </div>
                </div>

                <div>
                  <h3 className="text-xl font-bold text-white mb-4">Disponibilité</h3>
                  <div className="flex items-center gap-4 text-white">
                    <div className="w-12 h-12 bg-white/10 flex items-center justify-center">
                      <Clock className="w-6 h-6 text-[#f7c52d]" />
                    </div>
                    <span>7 jours / 7</span>
                  </div>
                </div>

                <div className="pt-6 border-t border-white/10">
                  <img src={LOGO_WHITE} alt="GOSTO TAXI" className="h-20 w-auto" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="footer py-12" data-testid="footer">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-4">
              <img src={LOGO_WHITE} alt="GOSTO TAXI" className="h-12 w-auto" />
            </div>
            
            <div className="text-center md:text-left text-slate-400 text-sm">
              <p>GOSTO TAXI - SIRET 993 322 619</p>
              <p>Taxi rattaché à la ville de Marseille - Conventionné CPAM</p>
            </div>

            <div className="flex items-center gap-6 text-sm">
              <Link to="/mentions-legales" className="text-slate-400 hover:text-[#f7c52d]" data-testid="link-mentions">
                Mentions légales
              </Link>
              <Link to="/politique-confidentialite" className="text-slate-400 hover:text-[#f7c52d]" data-testid="link-confidentialite">
                Politique de confidentialité
              </Link>
            </div>
          </div>

          <div className="mt-8 pt-8 border-t border-white/10 text-center text-slate-500 text-sm">
            © 2025 GOSTO TAXI. Tous droits réservés.
          </div>
        </div>
      </footer>

      {/* Mobile CTA */}
      <div className="mobile-cta md:hidden" data-testid="mobile-cta">
        <a
          href={PHONE_LINK}
          className="block w-full bg-[#f7c52d] text-[#1a365d] font-bold text-center py-4 uppercase tracking-wide"
        >
          <Phone className="inline w-5 h-5 mr-2" />
          Appeler maintenant
        </a>
      </div>
    </div>
  );
}
